import { useState, useCallback } from 'react';
import { useUpdateUserRoleMutation } from '../../../api/userApi2';
import { useUserStore } from '../../../app/store/useUserStore';
import { useSearchStore } from '../../../app/store/useSearchStore';
import { trackException } from '../../../shared/utils/telemetry';
import { useDispatch } from 'react-redux';
import { loadNotifications } from '../../../app/store/slices/notificationSlice';
import { UserRole, getRoleIdFromDefaultRoles, toggleRole } from '../../../types/user';

interface UseRoleSwitchReturn {
    switchRole: () => Promise<boolean>;
    isSwitching: boolean;
    error: string | null;
    clearError: () => void;
}

export const useRoleSwitch = (): UseRoleSwitchReturn => {
    const [isSwitching, setIsSwitching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const updateUserRoleMutation = useUpdateUserRoleMutation();
    const { user, updateUserRole, defaultRoles } = useUserStore();
    const { setUserRole, reset: resetSearch } = useSearchStore();
    const dispatch = useDispatch();

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const switchRole = useCallback(async (): Promise<boolean> => {
        // Validation
        if (!user?.user_id) {
            const errorMsg = 'User not authenticated';
            setError(errorMsg);
            return false;
        }

        if (!user.user_role) {
            const errorMsg = 'Current role not set';
            setError(errorMsg);
            return false;
        }

        if (!defaultRoles.length) {
            const errorMsg = 'Role configuration not loaded';
            setError(errorMsg);
            return false;
        }

        // Determine new role using enum
        const currentRole = user.user_role as UserRole;
        const newRole = toggleRole(currentRole);
        const newRoleId = getRoleIdFromDefaultRoles(newRole, defaultRoles);

        setIsSwitching(true);
        setError(null);

        try {
            // 1. Update backend FIRST
            await updateUserRoleMutation.mutateAsync({
                user_id: user.user_id,
                role_id: newRoleId,
            });

            // 2. Backend succeeded - update local state
            updateUserRole(newRole);
            resetSearch();
            setUserRole(newRole);

            // 3. Reload notifications for the new role
            dispatch(loadNotifications());

            // Return success
            return true;

        } catch (err) {
            const errorMessage = err instanceof Error
                ? err.message
                : 'Failed to switch role. Please try again.';

            trackException(err as Error, 'useRoleSwitch');
            setError(errorMessage);
            return false;

        } finally {
            setIsSwitching(false);
        }
    }, [user, defaultRoles, updateUserRoleMutation, updateUserRole, setUserRole, resetSearch, dispatch]);

    return {
        switchRole,
        isSwitching,
        error,
        clearError
    };
};
